import React, { useState } from 'react';

import { useTranslation, useAppDispatch, useAppSelector, useGAEventCommonProps } from 'client/hooks';
import { selectRoomDbData, selectRoomId } from 'client/redux/room/selectors';
import { selectUserRoomRole } from 'client/redux/client/selectors';
import { analytics } from 'client/utils/analytics';
import { ROOM_STATUS, USER_ROLES } from 'constants/index';

import Icon from '../../Icon';
import Button from '../../Button';
import PopupWrapper from '../PopupWrapper';
import TransitionComponent from '../../TransitionComponent';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';

import css from './TimeOutPopup.module.scss';

const TimeOutPopup = () => {
  const { translate } = useTranslation('popUps');
  const [popupActivity, setPopupActivity] = useState<boolean>(false);
  const [fiveMinPopupActivity, setFiveMinPopupActivity] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const roomData = useAppSelector(selectRoomDbData);
  const isRoomFinished = roomData?.status === ROOM_STATUS.FINISHED;
  const roomId = useAppSelector(selectRoomId);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomId));
  const isListener = userRole === USER_ROLES.LISTENER;
  const { common } = useGAEventCommonProps();

  const onPopupClick = (isOpen: boolean) => (): void => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: 'Close popup: Time out',
    });

    if (popupActivity) {
      setPopupActivity(isOpen);
    }

    if (fiveMinPopupActivity) {
      setFiveMinPopupActivity(isOpen);
    }
    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  const onAgreeBtnClick = () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('timeOutPopup.button'),
    });

    if (popupActivity) {
      setPopupActivity(false);
    }

    if (fiveMinPopupActivity) {
      setFiveMinPopupActivity(false);
    }

    dispatch(setIsShowFooterPopUpOverlay(false));
  };

  // Show popup when will change some dependencies
  React.useEffect(() => {
    if (roomData && roomData.date && roomData.durationLimit && !isRoomFinished && !isListener) {
      const dateOfStart: number = new Date(roomData?.date).valueOf();
      const durationLimitMillSec = Number(roomData?.durationLimit) * 60 * 1000;
      const fifteenMinRemaining = 15 * 60 * 1000;
      const fiveMinRemaining = 5 * 60 * 1000;

      // Show popup when time now between 15 min of the end. Doesnt show popup if time remaining more then 15 min, or if current time more then duration for room
      const intervalId = setInterval(() => {
        const dateNow: number = Date.now();
        if (
          durationLimitMillSec - (dateNow - dateOfStart) <= fifteenMinRemaining &&
          durationLimitMillSec - (dateNow - dateOfStart) >= fiveMinRemaining
        ) {
          setPopupActivity(true);
          dispatch(setIsShowFooterPopUpOverlay(true));
          clearInterval(intervalId);
        }
      }, 1000);

      const secondIntervalId = setInterval(() => {
        const dateNow: number = Date.now();
        if (durationLimitMillSec - (dateNow - dateOfStart) <= fiveMinRemaining) {
          setFiveMinPopupActivity(true);
          dispatch(setIsShowFooterPopUpOverlay(true));
          clearInterval(secondIntervalId);
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
        clearInterval(secondIntervalId);
      };
    }
  }, [roomData, dispatch, isListener]);

  return (
    <>
      <TransitionComponent type="opacity" visible={popupActivity || fiveMinPopupActivity}>
        <PopupWrapper onClose={onPopupClick(false)}>
          <div className={css.container}>
            <div className={css.icon}>
              <Icon type="clock" />
            </div>
            <div className={css.title}>
              <span>{translate('timeOutPopup.title')}</span>
            </div>
            <div className={css.subtitle}>
              <span>
                {fiveMinPopupActivity ? translate('timeOutPopup.fiveMinSubtitle') : translate('timeOutPopup.subtitle')}
              </span>
            </div>

            <Button className={css.btn} label={translate('timeOutPopup.button')} onClickHandler={onAgreeBtnClick} />
          </div>
        </PopupWrapper>
      </TransitionComponent>
    </>
  );
};

export default TimeOutPopup;
